import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Sparkles, Trophy, Wallet, Briefcase, Heart, Zap, GraduationCap, Home, FileText, Users } from "lucide-react";

const HERO_3D = "/hero-rocket.png";
const COIN_3D = "https://static.prod-images.emergentagent.com/jobs/e4d61cd5-da6a-4581-9978-422c2e6291f5/images/be91adb3176defefa9b1f811fbfd217342f38262c22d55c626a736a00f7bbf9d.png";
const CHECKLIST_3D = "https://static.prod-images.emergentagent.com/jobs/e4d61cd5-da6a-4581-9978-422c2e6291f5/images/e44512ac48855be07c3f4319b50972748c25360e8509b963a0c135f8b7c78d41.png";
const MENTOR_3D = "/keen-cheer.png";

const FEATURES = [
  { icon: Wallet,         color: "#FFD700", title: "Money & Banking",            desc: "Bank, budget, super, tax — sorted." },
  { icon: Briefcase,      color: "#FF2A6D", title: "Work & Career",              desc: "Resume, interviews, first job pay rights." },
  { icon: GraduationCap,  color: "#05D9E8", title: "Study & University",         desc: "Choose, apply, fund & survive uni or TAFE." },
  { icon: Home,           color: "#01FFC3", title: "Home & Living",              desc: "Renting, bills, moving out without losing it." },
  { icon: Heart,          color: "#FF2A6D", title: "Health & Wellbeing",         desc: "Medicare, mental health, sleep & food." },
  { icon: FileText,       color: "#FFD700", title: "Legal & Admin",              desc: "ID, contracts, voting, knowing your rights." },
  { icon: Users,          color: "#01FFC3", title: "Relationships & Life Skills",desc: "Boundaries, friendships, cooking, conflict." },
  { icon: Zap,            color: "#05D9E8", title: "Digital Life",               desc: "Cyber-safety, scams & AI literacy." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-aurora text-white overflow-x-hidden">
      {/* nav */}
      <nav className="flex items-center justify-between px-6 md:px-12 py-6 max-w-7xl mx-auto">
        <div className="font-display text-2xl md:text-3xl font-black tracking-tight" data-testid="brand-logo">
          <span className="text-gradient-pink">S</span><span className="text-neoncyan">0</span><span className="text-gradient-pink">rted</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-white/80 hover:text-white font-semibold text-sm" data-testid="nav-login">Sign in</Link>
          <Link to="/register" className="btn-primary-neon text-sm" data-testid="nav-register">Get started</Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative px-6 md:px-12 py-12 md:py-20 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
              <Sparkles size={14} className="text-neoncyan"/>
              <span className="text-xs font-semibold uppercase tracking-widest text-white/80">Built for 16–30 year olds in Australia</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              Adulting <span className="text-gradient-pink">isn't taught</span><br/>
              in school.<br/>
              <span className="text-gradient-cyan">So we built the class.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/70 max-w-xl">
              Bite-sized checklists, real Aussie info, an AI mentor who actually helps —
              and points so it doesn't feel like homework.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" className="btn-primary-neon flex items-center gap-2" data-testid="hero-get-started">
                Start free <ArrowRight size={18}/>
              </Link>
              <Link to="/about" className="btn-secondary-neon" data-testid="hero-about">How it works</Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-white/60 flex-wrap">
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-neongreen"/> 100% free</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-neongreen"/> Privacy-first</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-neongreen"/> Aussie content</div>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative w-full max-w-md mx-auto aspect-square">
              {/* Tiny brand-coloured drifting dots */}
              <span className="absolute top-[12%] left-[8%] w-3 h-3 rounded-full bg-neonpink animate-drift-slow" aria-hidden="true"/>
              <span className="absolute top-[18%] right-[10%] w-2 h-2 rounded-full bg-neoncyan animate-drift-slower" aria-hidden="true"/>
              <span className="absolute bottom-[20%] left-[12%] w-2.5 h-2.5 rounded-full bg-neonyellow animate-drift-slow" aria-hidden="true"/>
              <span className="absolute bottom-[12%] right-[14%] w-2 h-2 rounded-full bg-neongreen animate-drift-slower" aria-hidden="true"/>
              {/* Hero rocket — gentle float, screen-blend makes pure-black bg invisible */}
              <img
                src={HERO_3D}
                alt="S0rted — launch your adult life"
                data-testid="hero-rocket-image"
                className="relative w-full h-full object-contain animate-float"
                style={{ mixBlendMode: "screen" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 md:px-12 py-20 max-w-7xl mx-auto">
        <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter mb-2">8 life categories.<br/><span className="text-gradient-pink">One adult, level by level.</span></h2>
        <p className="text-white/60 max-w-xl mb-10">From opening a bank account to lodging tax, we'll walk you through it all — with trusted Australian links.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="glass-card p-6 hover:scale-[1.03] hover:border-white/20 transition" data-testid={`feature-card-${i}`}>
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                   style={{background:`${f.color}22`, color:f.color, boxShadow:`0 0 22px ${f.color}33`}}>
                <f.icon/>
              </div>
              <div className="font-display font-bold text-lg">{f.title}</div>
              <div className="text-sm text-white/65 mt-1">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* AI Mentor */}
      <section className="px-6 md:px-12 py-20 max-w-7xl mx-auto">
        <div className="glass-card p-8 md:p-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-neoncyan/15 text-neoncyan text-xs font-bold mb-3">
              <Sparkles size={12}/> AI MENTOR
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">Meet Keen. Always around. <span className="text-gradient-cyan">Never patronising.</span></h2>
            <p className="text-white/70 mt-4">A floating chat that helps you prep for interviews, decode your payslip, find a doctor, draft a polite email to your landlord — whatever you're stuck on.</p>
            <ul className="mt-5 space-y-2 text-sm text-white/80">
              <li className="flex gap-2"><CheckCircle2 size={18} className="text-neongreen flex-shrink-0"/> Knows your level & goals</li>
              <li className="flex gap-2"><CheckCircle2 size={18} className="text-neongreen flex-shrink-0"/> References Aussie services (ATO, Medicare, Fair Work)</li>
              <li className="flex gap-2"><CheckCircle2 size={18} className="text-neongreen flex-shrink-0"/> Never gives legal/financial/medical advice</li>
            </ul>
          </div>
          <div className="relative flex justify-center">
            <div className="relative w-72 h-72 rounded-full bg-gradient-to-br from-neoncyan/20 via-neonpink/10 to-neonyellow/15 flex items-center justify-center animate-float overflow-hidden ring-2 ring-white/10 shadow-[0_25px_80px_rgba(5,217,232,0.35)]">
              <img src={MENTOR_3D} alt="Keen AI Mentor" className="w-full h-full object-cover scale-110"/>
            </div>
          </div>
        </div>
      </section>

      {/* Gamification */}
      <section className="px-6 md:px-12 py-20 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="glass-card p-6 text-center">
            <Trophy className="mx-auto text-neonyellow mb-3" size={32}/>
            <div className="font-display text-3xl font-black">Earn XP</div>
            <p className="text-white/65 text-sm mt-2">Tick off real-world tasks. Watch your level fly.</p>
          </div>
          <div className="glass-card p-6 text-center">
            <Sparkles className="mx-auto text-neonpink mb-3" size={32}/>
            <div className="font-display text-3xl font-black">Unlock badges</div>
            <p className="text-white/65 text-sm mt-2">Streaks, milestones, category masteries.</p>
          </div>
          <div className="glass-card p-6 text-center">
            <Zap className="mx-auto text-neoncyan mb-3" size={32}/>
            <div className="font-display text-3xl font-black">Score perks</div>
            <p className="text-white/65 text-sm mt-2">Real discounts and free resources from partners.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 py-20 max-w-4xl mx-auto text-center">
        <h2 className="font-display text-4xl md:text-6xl font-black tracking-tighter">Ready to <span className="text-gradient-pink">level up?</span></h2>
        <p className="text-white/70 mt-4 text-lg">Free forever. No payment. No spam.</p>
        <Link to="/register" className="btn-primary-neon mt-8 inline-flex items-center gap-2 text-base" data-testid="cta-bottom-register">
          Start your roadmap <ArrowRight size={18}/>
        </Link>
      </section>

      <footer className="border-t border-white/5 py-10 px-6 md:px-12 max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-4 text-sm text-white/50">
        <div>© {new Date().getFullYear()} S0rted. Made for Aussie young adults.</div>
        <div className="flex gap-6 flex-wrap">
          <a href="mailto:hello@s0rted.io" className="hover:text-neoncyan" data-testid="footer-contact">hello@s0rted.io</a>
          <Link to="/about" className="hover:text-white" data-testid="footer-about">About</Link>
          <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy & Safety</Link>
          <Link to="/sponsor" className="hover:text-neonyellow font-semibold" data-testid="footer-sponsor">For Brands →</Link>
        </div>
      </footer>
    </div>
  );
}
